import { ESCAPE_PRIORITY, isPrimaryModifier, useEscapeAction } from '@peruselab/structure-editor/components';
import { useEffect } from 'react';
import {
  emptySelection,
  hasSelection,
  type EditorSelection,
  type EditorTool
} from '@peruselab/structure-editor/domain/editor';
import type { DrawingDocument } from '../generated/drawing-contract';

/**
 * Keeps tool/selection state consistent with the document for useDrawingDocument.ts (roadmap item
 * 35). Selection is not part of undo/redo history (see docs/repo-topology-phase-5-use-drawing-
 * document-decomposition.md), so these effects are what makes undo/redo, deletion, and an emptied
 * canvas "just work" without stale atom/bond ids or a stuck tool.
 */
export function useSelectionReconciliation(
  document: DrawingDocument,
  selection: EditorSelection,
  setSelection: (next: EditorSelection) => void,
  setTool: (next: EditorTool) => void,
  deleteSelection: () => void,
  copySelection: () => void,
  cutSelection: () => void,
  pasteClipboard: () => void,
  undo: () => void,
  redo: () => void,
  copyStyle: () => boolean = () => false,
  pasteStyle: () => boolean = () => false
) {
  // #762 -- Escape clears the selection only when nothing earlier in the order (an unfinished
  // gesture, an open menu/dialog) took the press: one action per press (escape arbiter).
  useEscapeAction(ESCAPE_PRIORITY.selection, () => {
    if (!hasSelection(selection)) return false;
    setSelection(emptySelection());
    return true;
  });
  useEffect(() => {
    const handleDeleteKey = (event: KeyboardEvent) => {
      const target = event.target;
      // #777 -- a checkbox or radio button has no Undo of its own: ⌘/Ctrl+Z on one (just ticked,
      // say Show Hydrogens) undoes the drawing, as on a button.
      const choice = target instanceof HTMLInputElement && (target.type === 'checkbox' || target.type === 'radio');
      const undoKey = isPrimaryModifier(event) && ['z', 'y'].includes(event.key.toLowerCase()) && !event.altKey;
      if (
        (target instanceof HTMLInputElement && !(choice && undoKey)) ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable) ||
        // #762 -- commands stay inactive while text is being composed (IME).
        event.isComposing
      )
        return;
      // #624 (accepted, drawing-ui-history-revisions.md) -- canvas Undo/Redo from the keyboard:
      // ⌘/Ctrl+Z, ⇧⌘/Ctrl+Shift+Z, and Ctrl+Y. Text fields keep their own Undo (the guard above;
      // the rich-text editor handles its session's steps itself, #762), and a press inside an open
      // dialog (Export, File drawer) never reaches the drawing behind it.
      const key = event.key.toLowerCase();
      if (isPrimaryModifier(event) && (key === 'z' || key === 'y') && !event.altKey) {
        if (target instanceof Element && target.closest('[role="dialog"], [aria-modal="true"]')) return;
        event.preventDefault();
        if (key === 'y' || event.shiftKey) redo();
        else undo();
        return;
      }
      if (isPrimaryModifier(event) && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setTool('select');
        setSelection({
          atomIds: document.structure.graph.nodes.map((node) => node.id),
          bondIds: document.structure.graph.edges.map((edge) => edge.id),
          annotationIds: document.annotations.map((annotation) => annotation.id)
        });
        return;
      }
      // #861 (#842 S8): Copy style / Paste style, ⌥⌘C / ⌥⌘V (Alt+Ctrl on Windows). Matched on the
      // physical key: on a Mac, Option changes event.key (Option+C is "ç").
      if (isPrimaryModifier(event) && event.altKey && (event.code === 'KeyC' || event.code === 'KeyV')) {
        if (event.code === 'KeyC' ? copyStyle() : pasteStyle()) event.preventDefault();
        return;
      }
      // #569: Cut/Copy/Paste keyboard shortcuts, the natural expectation once the buttons exist.
      if (isPrimaryModifier(event) && event.key.toLowerCase() === 'c') {
        if (!hasSelection(selection)) return;
        event.preventDefault();
        copySelection();
        return;
      }
      if (isPrimaryModifier(event) && event.key.toLowerCase() === 'x') {
        if (!hasSelection(selection)) return;
        event.preventDefault();
        cutSelection();
        return;
      }
      if (isPrimaryModifier(event) && event.key.toLowerCase() === 'v') {
        // #981 -- inside an open dialog (the File drawer takes a pasted image) the browser's own
        // paste goes ahead; it never pastes into the drawing behind.
        if (target instanceof Element && target.closest('[role="dialog"], [aria-modal="true"]')) return;
        event.preventDefault();
        pasteClipboard();
        return;
      }
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (!hasSelection(selection)) return;
      event.preventDefault();
      deleteSelection();
    };
    globalThis.addEventListener('keydown', handleDeleteKey);
    return () => globalThis.removeEventListener('keydown', handleDeleteKey);
  }, [
    copySelection,
    copyStyle,
    cutSelection,
    deleteSelection,
    document.annotations,
    document.structure.graph.edges,
    document.structure.graph.nodes,
    pasteClipboard,
    pasteStyle,
    redo,
    selection,
    setSelection,
    setTool,
    undo
  ]);

  useEffect(() => {
    const atomIds = selection.atomIds.filter((id) => document.structure.graph.nodes.some((node) => node.id === id));
    const bondIds = selection.bondIds.filter((id) => document.structure.graph.edges.some((edge) => edge.id === id));
    const annotationIds = (selection.annotationIds ?? []).filter((id) =>
      document.annotations.some((annotation) => annotation.id === id)
    );
    if (
      annotationIds.length !== (selection.annotationIds?.length ?? 0) ||
      atomIds.length !== selection.atomIds.length ||
      bondIds.length !== selection.bondIds.length
    )
      setSelection({ atomIds, bondIds, annotationIds });
  }, [document.annotations, document.structure.graph.edges, document.structure.graph.nodes, selection, setSelection]);
}
